import { buildTooltipWrapper, TOOLTIP_CLICK_HINT_ICON_SVG } from '../shared/tooltip';
import type { ChartDataItem, EChartsTheme } from '../types';
import type { SpendingTrendBarChartConfig, SpendingTrendTooltipTheme } from './types';

const TOOLTIP_WIDTH_PX = 280;

type TooltipParam = { dataIndex?: number };

// widthStyle is a raw CSS fragment: the default card is fixed-width, stackedExpense shrinks to content.
const cardShellStyle = (echartsTheme: EChartsTheme, cardTheme: SpendingTrendTooltipTheme, widthStyle: string): string =>
  `color:${echartsTheme.textSecondaryColor};background-color:${cardTheme.cardBackground};backdrop-filter:${cardTheme.cardBackdropFilter};` +
  `border-radius:${echartsTheme.tooltipBorderRadius}px;box-shadow:${cardTheme.cardShadow};padding:${cardTheme.cardPadding}px;` +
  `text-align:left;overflow:visible;${widthStyle}`;

// Taller gap than shared buildTooltipDivider — not reusable without a visible spacing change.
const dividerStyle = (echartsTheme: EChartsTheme): string =>
  `width:100%;height:2px;border-bottom-width:1px;border-bottom-style:solid;border-bottom-color:${echartsTheme.dividerColor};margin:8px 0;`;

const hintRow = (echartsTheme: EChartsTheme, cardTheme: SpendingTrendTooltipTheme, text: string): string =>
  `<div style="display:flex;align-items:center;gap:8px;font-size:12px;line-height:16px;color:${echartsTheme.textTertiaryColor};">` +
  `<span aria-hidden="true" style="display:block;width:18px;height:18px;color:${cardTheme.iconColor};">${TOOLTIP_CLICK_HINT_ICON_SVG}</span>` +
  `<span>${text}</span></div>`;

/**
 * Two fixed tooltip layouts (default income/spend card, and the drawer's stackedExpense percent
 * card) driven by named fields already present on each row (`income`/`totalSpent`/`remaining`, or
 * `percentOfSpendLabel`/`transactionCountLabel`) rather than iterating `orderedFields` generically —
 * both current consumers use exactly one field, and the layouts are not meant to generalize past that.
 */
export const buildSpendingTrendTooltipFormatter = (
  data: ChartDataItem[],
  orderedFields: string[],
  config: SpendingTrendBarChartConfig,
) => {
  const {
    formatAmount,
    privacyMask,
    tooltipVariant,
    theme: echartsTheme,
    tooltipTheme: cardTheme,
    hideCents = false,
  } = config;
  const amountFormat = hideCents ? '0,0' : '0,0.00';
  const fmt = (value: unknown): string => String(privacyMask(formatAmount(Number(value) || 0, 'USD', amountFormat)));

  // Theme/variant never change mid-hover, so compute once instead of per mouse-move.
  const isStackedExpense = tooltipVariant === 'stackedExpense';
  const cardStyle = cardShellStyle(
    echartsTheme,
    cardTheme,
    isStackedExpense ? 'min-width:220px;' : `width:${TOOLTIP_WIDTH_PX}px;max-width:${TOOLTIP_WIDTH_PX}px;`,
  );
  const dividerHtml = `<div style="${dividerStyle(echartsTheme)}"></div>`;
  // Same fixed-literal reasoning as cardStyle/dividerHtml; stackedExpense's hint depends on the
  // row instead, so it stays computed per-invocation below.
  const staticHintHtml = isStackedExpense ? '' : hintRow(echartsTheme, cardTheme, 'Click to view breakdown');

  return (params: unknown): string => {
    const p = (Array.isArray(params) ? params[0] : params) as TooltipParam;
    const dataIndex = p?.dataIndex;
    const row = typeof dataIndex === 'number' ? data[dataIndex] : undefined;
    if (!row) return '';

    const categoryLabel = String(row.categoryLabel ?? row.category ?? '');
    const incompleteLabel = String(row.incompleteLabel ?? '');

    if (isStackedExpense) {
      const primaryField = orderedFields[0];
      const valueLabel = fmt(row[primaryField]);
      const body = `<div style="display:flex;align-items:baseline;gap:6px;color:${echartsTheme.textColor};font-size:14px;line-height:20px;">
          <span style="font-size:14px;font-weight:500;line-height:20px;">${categoryLabel}</span>
          <span style="font-size:12px;color:${echartsTheme.textSecondaryColor};">${incompleteLabel}</span>
        </div>
        <div style="margin-top:4px;display:flex;align-items:center;justify-content:flex-start;font-size:14px;color:${echartsTheme.textColor};gap:6px;">
          <span>${valueLabel}</span><span>(${String(row.percentOfSpendLabel ?? '')})</span>
        </div>`;
      return buildTooltipWrapper(
        `<div style="${cardStyle}">${body}${dividerHtml}${hintRow(echartsTheme, cardTheme, String(row.transactionCountLabel ?? ''))}</div>`,
      );
    }

    const body = `<div style="display:flex;align-items:baseline;gap:6px;color:${echartsTheme.textColor};font-size:14px;line-height:20px;">
        <span style="font-size:16px;font-weight:700;">${categoryLabel}</span>
        <span style="font-size:14px;color:${echartsTheme.textSecondaryColor};">${incompleteLabel}</span>
      </div>
      <div style="margin-top:8px;display:grid;row-gap:4px;">
        <div style="display:flex;align-items:center;justify-content:space-between;font-size:14px;color:${echartsTheme.textColor};">
          <span>Income</span><span style="color:${cardTheme.incomeColor};">${fmt(row.income)}</span>
        </div>
        <div style="display:flex;align-items:center;justify-content:space-between;font-size:14px;color:${echartsTheme.textColor};">
          <span>Total spent</span><span>${fmt(row.totalSpent)}</span>
        </div>
        <div style="display:flex;align-items:center;justify-content:space-between;font-size:14px;color:${echartsTheme.textColor};">
          <span>Remaining</span><span>${fmt(row.remaining)}</span>
        </div>
      </div>`;
    return buildTooltipWrapper(`<div style="${cardStyle}">${body}${dividerHtml}${staticHintHtml}</div>`);
  };
};
